import { useState, useRef, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
  Input,
  Label,
  Checkbox,
} from "@repo/ui";
import { Shield, Loader2, ArrowLeft, AlertTriangle } from "lucide-react";

import { useAuth } from "@/lib/auth/auth-provider";
import { verifyPharmacy2faLogin, verifyStaff2faLogin } from "@/lib/api/auth";

export const Route = createFileRoute("/_auth/2fa-verify")({
  component: TwoFactorVerifyPage,
  validateSearch: (search: Record<string, unknown>) => ({
    type: (search.type as "pharmacy" | "staff") ?? "pharmacy",
  }),
});

function TwoFactorVerifyPage() {
  const navigate = useNavigate();
  const { refetch } = useAuth();
  const { type } = Route.useSearch();

  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [remainingCodes, setRemainingCodes] = useState<number | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [useRecovery]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const verifyFn =
        type === "pharmacy" ? verifyPharmacy2faLogin : verifyStaff2faLogin;
      const codeType = useRecovery ? "recovery" : "totp";
      const data = (await verifyFn({
        code,
        type: codeType,
        rememberDevice,
      })) as {
        success?: boolean;
        needsFirstUser?: boolean;
        remainingCodes?: number;
        user?: {
          id: string;
          role: string;
          full_name: string;
          initials: string;
        };
      };

      if (data.remainingCodes !== undefined) {
        setRemainingCodes(data.remainingCodes);
      }

      if (type === "pharmacy") {
        refetch().catch(() => {});
        if (data.needsFirstUser) {
          navigate({ to: "/dashboard" });
        } else {
          navigate({ to: "/select-user" });
        }
      } else {
        if (data.user) {
          sessionStorage.setItem("activeUser", JSON.stringify(data.user));
        }
        await refetch();
        navigate({ to: "/dashboard" });
      }
    } catch (err) {
      const apiError = err as { message?: string; error?: string };
      const errorMessage =
        apiError.error || apiError.message || "Invalid verification code";

      // If max attempts exceeded, redirect back to login
      if (errorMessage.includes("Too many failed attempts")) {
        setError(errorMessage);
        setTimeout(() => {
          if (type === "pharmacy") {
            navigate({ to: "/login" });
          } else {
            navigate({ to: "/select-user" });
          }
        }, 2000);
      } else {
        setError(errorMessage);
        setCode("");
        inputRef.current?.focus();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (type === "pharmacy") {
      navigate({ to: "/login" });
    } else {
      navigate({ to: "/select-user" });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Two-Factor Authentication</CardTitle>
          <CardDescription>
            {useRecovery
              ? "Enter one of your recovery codes"
              : "Enter the 6-digit code from your authenticator app"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {remainingCodes !== null && remainingCodes <= 3 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  You have {remainingCodes} recovery{" "}
                  {remainingCodes === 1 ? "code" : "codes"} remaining.
                  Regenerate your codes from Settings.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="2fa-code">
                {useRecovery ? "Recovery Code" : "Verification Code"}
              </Label>
              {useRecovery ? (
                <Input
                  ref={inputRef}
                  id="2fa-code"
                  type="text"
                  placeholder="XXXX-XXXX"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  maxLength={9}
                  disabled={isLoading}
                  autoComplete="off"
                  className="text-center text-lg tracking-widest font-mono"
                />
              ) : (
                <Input
                  ref={inputRef}
                  id="2fa-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  maxLength={6}
                  disabled={isLoading}
                  autoComplete="one-time-code"
                  className="text-center text-lg tracking-widest font-mono"
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="remember-device"
                checked={rememberDevice}
                onCheckedChange={(checked) =>
                  setRememberDevice(checked === true)
                }
              />
              <Label
                htmlFor="remember-device"
                className="text-sm font-normal text-muted-foreground cursor-pointer"
              >
                Remember this device for 30 days
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={
                isLoading || (useRecovery ? code.length < 9 : code.length !== 6)
              }
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify"
              )}
            </Button>

            <div className="flex items-center justify-between">
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                onClick={() => {
                  setUseRecovery(!useRecovery);
                  setCode("");
                  setError("");
                }}
              >
                {useRecovery ? "Use authenticator code" : "Use a recovery code"}
              </button>

              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline flex items-center gap-1"
                onClick={handleCancel}
              >
                <ArrowLeft className="h-3 w-3" />
                Back
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
